/** SUNTRONIX ONE — Administration → ONE Connect → Telephony.
 * Provider-independent config (EXOTEL / SERVETEL / SYNAPSE / MANUAL). */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { PhoneCall, Plug, Webhook, ListChecks } from "lucide-react";

const input = "mt-1 w-full text-[12px] border border-slate-200 rounded-md px-2 py-1.5";

const PROVIDER_FIELDS = {
  EXOTEL: [["account_sid", "Account SID"], ["api_key", "API Key"], ["api_token", "API Token"], ["subdomain", "Subdomain (api.in.exotel.com)"]],
  SERVETEL: [["api_key", "API Key"], ["auth_token", "Auth Token"]],
  SYNAPSE: [["app_id", "App ID"], ["api_key", "API Key"], ["auth_token", "Auth Token"]],
  MANUAL: [],
};

export default function TelephonyConfig() {
  const [st, setSt] = useState(null);
  const [cfg, setCfg] = useState({ provider: "MANUAL", caller_id_number: "", numbers: "",
    routing: "", recording_enabled: false, recording_consent_required: true, webhook_url: "",
    api_key: "", api_token: "", account_sid: "", app_id: "", auth_token: "", subdomain: "" });
  const [outcomes, setOutcomes] = useState([]);
  const [outcomeText, setOutcomeText] = useState("");

  const load = useCallback(() => {
    api.get("/telephony/status").then(({ data }) => setSt(data)).catch(() => {});
    api.get("/telephony/config").then(({ data }) => {
      if (data.config) setCfg((p) => ({ ...p, ...data.config,
        numbers: (data.config.numbers || []).join(", "),
        api_key: "", api_token: "", account_sid: "", app_id: "", auth_token: "" }));
    }).catch(() => {});
    api.get("/telephony/outcomes").then(({ data }) => {
      setOutcomes(data.outcomes || []); setOutcomeText((data.outcomes || []).join("\n"));
    }).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      await api.put("/telephony/config", {
        ...cfg,
        numbers: cfg.numbers.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Configuration saved (credentials encrypted at rest)"); load();
    } catch (e) { toast.error(JSON.stringify(e.response?.data?.detail) || "Save failed"); }
  };
  const testConn = async () => {
    try { const { data } = await api.post("/telephony/test-connection");
      (data.ok ? toast.success : toast.error)(data.verdict); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Test failed"); }
  };
  const saveOutcomes = async () => {
    try {
      const list = outcomeText.split("\n").map((s) => s.trim()).filter(Boolean);
      const { data } = await api.put("/telephony/outcomes", { outcomes: list });
      setOutcomes(data.outcomes); toast.success("Outcomes saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const fields = PROVIDER_FIELDS[cfg.provider] || [];

  return (
    <div className="mx-auto max-w-[1000px] space-y-4" data-testid="telephony-config-page">
      <header className="flex items-center gap-3">
        <div className="grid h-10 w-10 place-items-center rounded-lg bg-teal-900 text-white"><PhoneCall className="h-5 w-5" /></div>
        <div className="flex-1">
          <div className="font-heading text-xl font-semibold">ONE Connect · Telephony</div>
          <div className="text-[11px] text-slate-500">Provider-independent telephony — caller ID, click-to-call, call logging</div>
        </div>
        {st && (
          <span data-testid="telephony-cfg-status" className={`rounded px-2 py-1 text-[10px] font-semibold ${st.status === "READY" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
            {st.status.replace(/_/g, " ")}
          </span>)}
      </header>

      {st?.can_configure ? (
        <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="telephony-cfg-card">
          <div className="mb-3 flex items-center gap-2 text-[12px] font-semibold"><Plug className="h-3.5 w-3.5" /> Provider configuration</div>
          <div className="grid gap-3 sm:grid-cols-3">
            <label className="text-[10px] text-slate-500">Provider
              <select value={cfg.provider} onChange={(e) => setCfg({ ...cfg, provider: e.target.value })} className={input} data-testid="telephony-provider">
                {["MANUAL", "EXOTEL", "SERVETEL", "SYNAPSE"].map((p) => <option key={p}>{p}</option>)}
              </select></label>
            <label className="text-[10px] text-slate-500">Caller ID number
              <input value={cfg.caller_id_number || ""} onChange={(e) => setCfg({ ...cfg, caller_id_number: e.target.value })} className={input + " font-mono"} data-testid="telephony-caller-id" /></label>
            <label className="text-[10px] text-slate-500">Numbers (comma separated)
              <input value={cfg.numbers} onChange={(e) => setCfg({ ...cfg, numbers: e.target.value })} className={input + " font-mono"} data-testid="telephony-numbers" /></label>
            {fields.map(([k, l]) => (
              <label key={k} className="text-[10px] text-slate-500">{l}
                <input type={k.includes("token") || k.includes("key") ? "password" : "text"} autoComplete="off"
                  value={cfg[k] || ""} onChange={(e) => setCfg({ ...cfg, [k]: e.target.value })}
                  className={input} data-testid={`telephony-${k}`}
                  placeholder={st?.configured ? "•••• (leave blank to keep)" : ""} /></label>
            ))}
            <label className="text-[10px] text-slate-500">Routing
              <input value={cfg.routing || ""} onChange={(e) => setCfg({ ...cfg, routing: e.target.value })} className={input} placeholder="e.g. round-robin / IVR flow id" data-testid="telephony-routing" /></label>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-4">
            <label className="flex items-center gap-2 text-[11px] text-slate-600">
              <input type="checkbox" checked={cfg.recording_enabled} onChange={(e) => setCfg({ ...cfg, recording_enabled: e.target.checked })} data-testid="telephony-recording" />
              Enable call recording (where provider &amp; law permit)
            </label>
            <label className="flex items-center gap-2 text-[11px] text-slate-600">
              <input type="checkbox" checked={cfg.recording_consent_required} onChange={(e) => setCfg({ ...cfg, recording_consent_required: e.target.checked })} data-testid="telephony-consent" />
              Require consent before recording
            </label>
          </div>

          <label className="mt-3 block text-[10px] text-slate-500"><Webhook className="mr-1 inline h-3 w-3" />Webhook URL (paste into provider dashboard)
            <input value={cfg.webhook_url || ""} onChange={(e) => setCfg({ ...cfg, webhook_url: e.target.value })} className={input + " font-mono"} placeholder="https://…/api/telephony/webhook/exotel" data-testid="telephony-webhook" /></label>

          <div className="mt-3 flex gap-2">
            <button onClick={save} data-testid="telephony-save" className="rounded-md bg-slate-900 px-3 py-1.5 text-[11px] font-semibold text-white">Save configuration</button>
            <button onClick={testConn} data-testid="telephony-test" className="rounded-md border border-teal-700 px-3 py-1.5 text-[11px] font-semibold text-teal-800">Test</button>
            {st?.last_test && <span className={`self-center text-[10px] ${st.last_test.ok ? "text-emerald-700" : "text-rose-700"}`}>{st.last_test.verdict}</span>}
          </div>
          <div className="mt-2 text-[9.5px] text-slate-400">Credentials are encrypted at rest and never returned to the browser. If no provider is configured the module shows SETUP REQUIRED (never hidden).</div>
        </div>
      ) : (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-[12px] text-amber-800">Requires management or super_admin to configure.</div>
      )}

      <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="telephony-outcomes-card">
        <div className="mb-2 flex items-center gap-2 text-[12px] font-semibold"><ListChecks className="h-3.5 w-3.5" /> Call outcomes ({outcomes.length})</div>
        <div className="text-[11px] text-slate-500">One outcome per line. Used across the call log.</div>
        <textarea value={outcomeText} onChange={(e) => setOutcomeText(e.target.value)} rows={6} className={input + " font-mono"} data-testid="telephony-outcomes-text" />
        {st?.can_configure && (
          <button onClick={saveOutcomes} data-testid="telephony-outcomes-save" className="mt-2 rounded-md bg-teal-800 px-3 py-1.5 text-[11px] font-semibold text-white">Save outcomes</button>
        )}
      </div>
    </div>
  );
}
